import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormControl,
  IconControl,
  Loading,
} from 'spark-core-dx/components';
import WizardFooter from '../resource/components/wizard/WizardFooter';
import ClientCommandStatusTag from './ClientCommandStatusTag';

const formatStatusValue = (value) => String(value || '')
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .trim();

const isDeploymentFailed = (workspace) => [
  'Failed',
  'DeploymentFailed',
  'MigrationFailed',
].includes(workspace?.DeploymentStatus)
  || [
    'DeploymentFailed',
    'MigrationFailed',
  ].includes(workspace?.LifecycleState);

const isDeploymentRunning = (workspace) => [
  'Queued',
  'Running',
].includes(workspace?.DeploymentStatus)
  || workspace?.LifecycleState === 'Deploying';

const getDeploymentDisplayStatus = (workspace) => {
  if (isDeploymentFailed(workspace)) return 'Failed';
  if (isDeploymentRunning(workspace)) return 'Deploying';
  if (
    workspace?.DeploymentStatus === 'Completed'
    || ['Ready', 'Enabled'].includes(workspace?.LifecycleState)
  ) {
    return 'Complete';
  }
  return formatStatusValue(workspace?.DeploymentStatus) || 'Not started';
};

const ReviewFact = ({ label, value, status = false }) => (
  <div className="command-onboarding__review-fact">
    <span>{label}</span>
    {status ? (
      <ClientCommandStatusTag status={value || 'Not provided'} />
    ) : (
      <strong>{value || 'Not provided'}</strong>
    )}
  </div>
);

const StepIntro = ({ step }) => (
  <div className="command-onboarding__step-intro">
    <div>
      <strong>{step.DisplayName}</strong>
      <span>{step.Description}</span>
    </div>
    {step.Status !== 'Current' ? (
      <ClientCommandStatusTag status={formatStatusValue(step.Status)} />
    ) : null}
  </div>
);

const StepEvidence = ({ workspace, step }) => (
  <>
    <div className="command-onboarding__review-grid">
      <ReviewFact
        label="Storage Mode"
        value={formatStatusValue(workspace?.RequestedStorageMode)}
      />
      <ReviewFact
        label="Migration Strategy"
        value={formatStatusValue(workspace?.MigrationStrategy)}
      />
      <ReviewFact
        label="Lifecycle"
        value={formatStatusValue(workspace?.LifecycleState)}
        status
      />
      <ReviewFact
        label="Routing"
        value={formatStatusValue(workspace?.RoutingState)}
      />
      <ReviewFact
        label="Last Operation"
        value={formatStatusValue(workspace?.LastOperationName) || 'Not run'}
      />
      <ReviewFact
        label="Last Result"
        value={formatStatusValue(workspace?.LastOperationState) || 'Not run'}
        status
      />
    </div>
    <p className="command-onboarding__phase-note">
      {step.Message || 'Review the current server-authoritative partition state.'}
    </p>
  </>
);

const DeploymentInventory = ({ workspace }) => {
  const inventoryLoaded = Boolean(
    workspace?.DeploymentInventoryLoaded
    || workspace?.DeploymentInventoryCapturedAtUtc,
  );
  const inventory = Array.isArray(workspace?.DeploymentInventory)
    ? [...workspace.DeploymentInventory]
    : [];
  inventory.sort((left, right) => {
    const recordDifference = Number(right.SourceRecordCount || 0)
      - Number(left.SourceRecordCount || 0);
    if (recordDifference !== 0) return recordDifference;
    return String(left.CollectionName || '').localeCompare(
      String(right.CollectionName || ''),
      undefined,
      { sensitivity: 'base' },
    );
  });
  const totalRecords = inventory.reduce(
    (total, entry) => total + Number(entry.SourceRecordCount || 0),
    0,
  );
  const migratesExistingData =
    workspace?.MigrationStrategy !== 'NewClientInitialization';

  return (
    <section className="command-partition-deployment-section">
      <div className="command-partition-deployment-section__heading">
        <div>
          <strong>Migration check</strong>
          <span>
            Supported client-only collections and this client’s current source
            record counts.
          </span>
        </div>
        {inventoryLoaded ? (
          <strong>{inventory.length}</strong>
        ) : (
          <ClientCommandStatusTag status="Not checked" />
        )}
      </div>
      {!inventoryLoaded ? (
        <p className="command-onboarding__phase-note">
          Select Next to validate the connection and load the current source
          record counts.
        </p>
      ) : null}
      {inventoryLoaded ? (
        <article className="command-partition-deployment-inventory__total">
          <div>
            <strong>Total source records</strong>
            <span>
              {migratesExistingData
                ? 'These records will be reconciled during deployment.'
                : 'New-client initialization is selected, so source records will not be migrated.'}
            </span>
          </div>
          <strong>
            {totalRecords.toLocaleString()}
            {' record(s)'}
          </strong>
        </article>
      ) : null}
      {inventoryLoaded && inventory.length ? (
        <div className="resource-workspace-record-list">
          {inventory.map((entry) => (
            <article
              className="resource-workspace-record-row"
              key={entry.CollectionName}
            >
              <div>
                <strong>{entry.CollectionName}</strong>
                <span>
                  {(entry.ItemTypes || []).length}
                  {' supported item type(s)'}
                </span>
              </div>
              <strong className="resource-workspace-record-row-status">
                {Number(entry.SourceRecordCount || 0).toLocaleString()}
                {' record(s)'}
              </strong>
            </article>
          ))}
        </div>
      ) : null}
      {inventoryLoaded && !inventory.length ? (
        <p className="command-onboarding__phase-note">
          No client records need to be migrated.
        </p>
      ) : null}
    </section>
  );
};

const DeploymentStatus = ({ workspace, eventManagerHref }) => {
  const logs = Array.isArray(workspace?.DeploymentLogs)
    ? workspace.DeploymentLogs
    : [];
  const deploymentFailed = isDeploymentFailed(workspace);
  return (
    <section className="command-partition-deployment-section command-partition-deployment-section--status">
      <div className="command-partition-deployment-section__heading">
        <div>
          <strong>Deployment status</strong>
          <span>
            Refresh this wizard to load the latest tracked status and error
            entries.
          </span>
        </div>
        <ClientCommandStatusTag
          status={getDeploymentDisplayStatus(workspace)}
        />
      </div>
      {workspace?.DeploymentMessage ? (
        <p
          className={
            deploymentFailed
              ? 'resource-wizard__alert'
              : 'command-onboarding__phase-note'
          }
          role={deploymentFailed ? 'alert' : 'status'}
        >
          {workspace.DeploymentMessage}
        </p>
      ) : null}
      {logs.length ? (
        <div className="command-partition-deployment-logs">
          {logs.map((entry, index) => (
            <div
              className={`command-partition-deployment-log command-partition-deployment-log--${String(entry.MessageType || 'status').toLowerCase()}`}
              key={entry.UniqueId || `${entry.CreatedAtUtc}-${index}`}
            >
              <span>
                {entry.CreatedAtUtc
                  ? new Date(entry.CreatedAtUtc).toLocaleString()
                  : 'Status'}
              </span>
              <strong>{entry.Message}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {eventManagerHref ? (
        <a
          className="command-partition-deployment-event-link"
          href={eventManagerHref}
        >
          View full event details
        </a>
      ) : null}
    </section>
  );
};

const ClientDatabasePartitionWizard = ({
  open,
  workspace,
  metadata,
  formFields,
  canSaveConfigurationPermission,
  canRunStep,
  busy,
  error,
  message,
  onRefresh,
  onSaveConfiguration,
  onRunStep,
  eventManagerHref,
}) => {
  const applicableSteps = useMemo(
    () => (workspace?.WizardSteps || [])
      .filter((step) => step.IsApplicable !== false)
      .sort((left, right) => Number(left.Order) - Number(right.Order)),
    [workspace?.WizardSteps],
  );
  const serverIndex = Math.max(
    0,
    Math.min(
      applicableSteps.length - 1,
      Number(workspace?.CurrentWizardStep || 1) - 1,
    ),
  );
  const [activeIndex, setActiveIndex] = useState(serverIndex);
  const [draft, setDraft] = useState(workspace);
  const saveNavigationRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const saveNavigation = saveNavigationRef.current;
    saveNavigationRef.current = null;
    if (saveNavigation !== 'stay') {
      setActiveIndex(serverIndex);
    }
    setDraft({
      ...workspace,
      DedicatedConnectionInput: '',
    });
  }, [
    open,
    serverIndex,
    workspace?.LastOperationCorrelationId,
    workspace?.RequestConcurrencyToken,
  ]);

  if (!workspace) return null;

  const activeStep = applicableSteps[activeIndex];
  const showingDeploymentStatus = Boolean(
    activeStep?.Key === 'deploy'
    && (
      [
        'Queued',
        'Running',
        'Failed',
        'Completed',
      ].includes(workspace?.DeploymentStatus)
      || [
        'Deploying',
        'DeploymentFailed',
        'MigrationFailed',
        'Ready',
      ].includes(workspace?.LifecycleState)
    )
  );
  const postSetup = [
    'Enabled',
    'RollbackRequired',
  ].includes(workspace.LifecycleState);
  const configurationEditable = Boolean(
    canSaveConfigurationPermission
    && workspace.CanEditRequest
    && !postSetup,
  );
  const browsingCompletedStep = activeIndex < serverIndex;
  const activeConfiguration =
    activeStep?.Key === 'configuration';
  const activeComplete = activeStep?.Key === 'complete';
  const canRunCurrentStep = Boolean(
    canRunStep?.(activeStep?.Key)
    && activeStep?.CanExecute
    && activeIndex === serverIndex,
  );
  const canSaveConfiguration = Boolean(
    activeConfiguration
    && configurationEditable
    && metadata
    && formFields?.length,
  );
  const nextReviewStep = applicableSteps[
    Math.min(activeIndex + 1, serverIndex)
  ];
  const deploymentFailed = isDeploymentFailed(workspace);
  const deploymentRunning = isDeploymentRunning(workspace);
  const stepAction = {
    identity: {
      description:
        'Save the immutable partition identity while routing remains on the shared PlatformApp database.',
    },
    configuration: {
      description:
        'Save the requested storage and migration settings, then advance to preparation.',
    },
    prepare: {
      description:
        'Start dedicated database preparation while routing remains on the shared PlatformApp database.',
    },
    validate: {
      description:
        'Validate the target connection and load the current migration inventory without changing routing.',
    },
    deploy: {
      description: deploymentFailed
        ? 'Retry database creation, schema and index setup, and record reconciliation while routing remains shared.'
        : 'Create the database, reconcile its schema and indexes, and copy existing client records while routing remains shared.',
    },
    activate: {
      description:
        'Reconcile remaining shared records, switch to dedicated routing, verify the target copies, and soft-delete the verified shared copies.',
    },
  }[activeStep?.Key];
  let nextActionDescription = '';
  if (browsingCompletedStep) {
    nextActionDescription =
      `Review the next completed step without running an operation. ${nextReviewStep?.DisplayName || 'The current lifecycle step'} remains server-authoritative.`;
  } else if (stepAction) {
    nextActionDescription = stepAction.description;
  }
  if (
    showingDeploymentStatus
    && deploymentRunning
    && !browsingCompletedStep
    && !canRunCurrentStep
  ) {
    nextActionDescription =
      'Remain disabled while deployment is running. Refresh Partition Status to load the latest result; Next will become available when the lifecycle can advance.';
  }
  const nextDisabled = Boolean(
    busy
    || !activeStep
    || (
      !activeComplete
      && !browsingCompletedStep
      && !canRunCurrentStep
      && !canSaveConfiguration
    )
  );

  const saveConfiguration = async (advance) => {
    if (!canSaveConfiguration || busy) return false;
    saveNavigationRef.current = advance ? 'advance' : 'stay';
    const result = await onSaveConfiguration(draft);
    if (!result?.Success) {
      saveNavigationRef.current = null;
      return false;
    }
    if (advance) {
      const savedWorkspace = result.Items?.[0];
      const savedIndex = Math.max(
        0,
        Math.min(
          applicableSteps.length - 1,
          Number(savedWorkspace?.CurrentWizardStep || serverIndex + 1) - 1,
        ),
      );
      setActiveIndex(savedIndex);
    }
    return true;
  };

  const runPrimaryAction = async () => {
    if (!activeStep || busy) return;
    if (activeConfiguration && canSaveConfiguration) {
      await saveConfiguration(true);
      return;
    }
    if (browsingCompletedStep) {
      setActiveIndex((current) => Math.min(
        serverIndex,
        current + 1,
      ));
      return;
    }
    if (canRunCurrentStep) {
      onRunStep(activeStep.Key);
    }
  };

  const renderStep = () => {
    if (!activeStep) {
      return (
        <div className="resource-wizard__alert" role="alert">
          Database partition wizard status is unavailable. Refresh and try again.
        </div>
      );
    }

    if (activeStep.Key === 'identity') {
      return (
        <>
          <StepIntro step={activeStep} />
          <div className="command-onboarding__review-grid">
            <ReviewFact
              label="Partition Key"
              value={workspace.PartitionKey || workspace.ProposedPartitionKey}
            />
            <ReviewFact
              label="Expected Database"
              value={
                workspace.ExpectedDatabaseName
                || workspace.ProposedDatabaseName
              }
            />
            <ReviewFact
              label="Identity Status"
              value={workspace.CapabilityStatus}
              status
            />
          </div>
          {activeStep.Message ? (
            <p className="command-onboarding__phase-note">
              {activeStep.Message}
            </p>
          ) : null}
        </>
      );
    }

    if (activeConfiguration) {
      const configurationStep = postSetup
        ? {
          ...activeStep,
          Description: `${activeStep.Description} Use the separate Edit action for post-setup changes.`,
        }
        : activeStep;

      return (
        <>
          <StepIntro step={configurationStep} />
          {metadata && formFields?.length ? (
            <FormControl
              metadata={metadata}
              formFields={formFields}
              itemData={draft}
              disableFooter
              disableMinWidth
              tabLayout="top"
              viewOnly={!configurationEditable}
              onChange={(value, fieldName) => {
                setDraft((current) => ({
                  ...current,
                  [fieldName]: value,
                }));
              }}
              onSave={() => {}}
            />
          ) : (
            <div className="resource-wizard__alert" role="alert">
              Partition request metadata is unavailable. Refresh and try again.
            </div>
          )}
        </>
      );
    }

    if (activeStep.Key === 'validate') {
      return (
        <>
          <StepIntro step={activeStep} />
          <StepEvidence workspace={workspace} step={activeStep} />
          <DeploymentInventory workspace={workspace} />
        </>
      );
    }

    if (activeStep.Key === 'deploy') {
      if (showingDeploymentStatus) {
        return (
          <>
            <StepIntro step={{
              ...activeStep,
              DisplayName: 'Deployment Status',
              Description:
                'Track database creation, collection and index setup, and any required record migration.',
              Status: getDeploymentDisplayStatus(workspace),
            }} />
            <DeploymentStatus
              workspace={workspace}
              eventManagerHref={eventManagerHref}
            />
          </>
        );
      }
      return (
        <>
          <StepIntro step={activeStep} />
          <StepEvidence workspace={workspace} step={activeStep} />
        </>
      );
    }

    return (
      <>
        <StepIntro step={activeStep} />
        <StepEvidence workspace={workspace} step={activeStep} />
      </>
    );
  };

  return (
    <div className="resource-wizard command-onboarding command-partition-wizard">
      <div className="resource-wizard__content vertical-scrollable">
        <div className="resource-wizard__identity-topline command-onboarding__status-header">
          <div className="command-onboarding__status-title" role="status">
            {workspace.ApplicationName || 'PlatformApp'}
            {' · '}
            {formatStatusValue(workspace.LifecycleState)}
            {' · '}
            {`Step ${activeIndex + 1} of ${Math.max(1, applicableSteps.length)}`}
          </div>
          <div className="command-onboarding__status-actions">
            <IconControl
              iconKey="refresh"
              title="Refresh Partition Status"
              aria-label="Refresh Partition Status"
              disabled={busy}
              onClick={onRefresh}
            />
          </div>
        </div>

        {busy ? (
          <div className="command-entry__loading" role="status">
            <Loading />
            <span>Working with the database partition lifecycle...</span>
          </div>
        ) : null}
        {error ? (
          <div className="resource-wizard__alert" role="alert">
            {error}
          </div>
        ) : null}
        {message ? (
          <div
            className="resource-wizard__alert resource-wizard__alert--success"
            role="status"
          >
            {message}
          </div>
        ) : null}

        {!busy ? (
          <section
            className={[
              'resource-wizard__step-panel',
              showingDeploymentStatus
                ? 'command-partition-wizard__deployment-step'
                : '',
            ].filter(Boolean).join(' ')}
            aria-label="Database partition setup steps"
          >
            {renderStep()}
            {!activeComplete ? (
              <p className="command-onboarding__phase-note">
                <strong>Next will: </strong>
                {nextActionDescription
                  || 'Remain disabled until the current server-authoritative step can run.'}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>

      {!busy && activeStep ? (
        <WizardFooter
          canCancelSetupFlow={false}
          showPreviousStepButton={activeIndex > 0}
          showSaveButton={false}
          showCompleteAction={false}
          hidePrimaryAction={false}
          isLastStep={activeComplete}
          isComplete={Boolean(workspace.WizardComplete)}
          saving={busy}
          hasActiveSectionStep
          activeSectionStepReadOnly={!canSaveConfiguration}
          nextDisabled={nextDisabled}
          primaryActionLabel="Next >>"
          onPrevious={() => setActiveIndex((current) => (
            Math.max(0, current - 1)
          ))}
          onSave={() => saveConfiguration(false)}
          onNext={runPrimaryAction}
          onComplete={runPrimaryAction}
        />
      ) : null}
    </div>
  );
};

export default ClientDatabasePartitionWizard;
